'use client';

import { createContext } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import type { SaasSubscriptionBasePlanType } from '@ps/models/lib/saas-subscription';
import type { CreatorInfo } from '@ps/types/creator';
import type { BehaviorSubject } from 'rxjs';

import type { CommunityChannel } from '../../types/CommunityChannel';

interface ChannelsState {
  channels: CommunityChannel[];
  isLoading: boolean;
  error: Error | undefined;
}

export interface CommunityContextValue {
  /**
   * Creator whose channels we're viewing (the STORE id — the Firestore
   * `stores/{creatorId}/...` root every data call keys on). On the dashboard
   * this is the signed-in user; on the visitor bio surface it's the bio
   * creator's id, passed into the provider.
   */
  creatorId: string | null | undefined;
  /**
   * Whether the signed-in viewer IS the creator (`currentUserId === creatorId`).
   * `true` for the dashboard owner AND for an owner previewing their own bio;
   * `false` for a visitor. Drives IDENTITY / ACCESS / PARTICIPATION the owner
   * keeps on EVERY surface — viewing their private/gated channels, composing,
   * unredacted post links, the "Your Channels" label. For MANAGEMENT gating use
   * {@link canManage}, not this.
   */
  isOwner: boolean;
  /**
   * Whether owner MANAGEMENT actions are available on THIS surface — create /
   * edit / delete channel, admin message actions (pin, delete-any), and
   * moderation (blocked-members view). `true` on the creator dashboard
   * (`= isOwner`); FORCED `false` on the visitor/member bio surface even for the
   * owner, so that tree is purely view + participate. Management there is
   * reached via a "Manage" entry point that routes to `/-/community`. Kept
   * distinct from {@link isOwner} so the owner-on-visitor experience matches a
   * member's (no half-working management controls).
   */
  canManage: boolean;
  /**
   * The creator's display name — shown on the visitor list header
   * ("<name>'s Channels") and the join/subscribe blocker. Undefined on the
   * dashboard (owner surface uses "Your Channels").
   */
  creatorName?: string;
  /**
   * The channels base — the prefix a channel/thread deep link is appended to
   * (`${basePath}/<slug>`): the dashboard `/-/community/channels` or the visitor
   * `/<url_code>/community/channels`. For "back to the channel list" use
   * {@link CommunityContextValue.homeHref}, which on both surfaces is the parent
   * `…/community` landing, not this base.
   */
  basePath: string;
  /**
   * The community landing ("back to the channel list") — the dashboard
   * `/-/community` or the visitor `/<url_code>/community`. Distinct from
   * {@link CommunityContextValue.basePath}: the list lives one segment ABOVE the
   * channels base.
   */
  homeHref: string;
  /**
   * The creator's bio context (facts) — the single source for building the
   * PUBLIC, shareable channel URL (`bioUrl(bioContext, { share: true })`).
   * Always present: on the dashboard it's the owner's
   * (`useMyPopStore().bioContext`); on the visitor bio surface it's the creator
   * being viewed.
   */
  bioContext: BioContext;
  channelsState$: BehaviorSubject<ChannelsState>;
  refreshChannels: () => Promise<void>;
  createChannelOpen$: BehaviorSubject<boolean>;
  /**
   * Whether the platform-subscription upsell modal is open. Shown instead of
   * the create form when the creator taps "Create Channel" without an active
   * platform subscription (server rejects with "no active subscription").
   */
  subscriptionUpsellOpen$: BehaviorSubject<boolean>;
  /**
   * The plan tier required to unlock the next channel (upstream's
   * `requiredBasePlanType`) — set by the gate when it opens the upsell, and
   * passed as the pre-selected plan to "Subscribe Now".
   */
  requiredUpsellTier$: BehaviorSubject<
    SaasSubscriptionBasePlanType | undefined
  >;
  /**
   * Owner intent to create a channel. Gated on the dashboard: opens the create
   * form when the creator's plan allows another channel, otherwise the
   * subscription upsell. Never invoked on the visitor surface — there the
   * create control is replaced by a "Manage" link to `/-/community` (see
   * {@link canManage}), and the create/upsell modals aren't mounted.
   */
  requestCreateChannel: () => void;
  /**
   * "Subscribe Now" action from the upsell — routes to the platform plans page
   * with the required tier pre-selected. Injected on the dashboard; `undefined`
   * on the visitor surface.
   */
  onSubscribeToPlatform?: () => void;
  /**
   * The signed-in visitor's subscription standing with the creator, already
   * follower-synthesized (a plain follow reads as FOLLOWER). Injected on the
   * visitor bio surface so the join flow can gate on subscription access;
   * `undefined` on the dashboard (owner) surface or while it resolves.
   */
  viewerSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  /**
   * Send the visitor to the subscribe blocker for a channel they can't yet
   * join (lacking the required subscription) — the "Subscribe Now and Join"
   * flow. Injected on the visitor surface; absent on the dashboard (the owner
   * never joins). When absent, the join flow falls back to attempting the join.
   */
  onRequireChannelSubscription?: (channel: CommunityChannel) => void;
  /**
   * The creator's unified {@link CreatorInfo} — threaded to the login-brand
   * handoff when a LOGGED-OUT viewer taps a gated action (joining a channel) so
   * the intercepted `/login` renders the creator's avatar + member sign-in copy
   * rather than the bare form. Present on the visitor surface (built from the bio
   * profile); `undefined` on the dashboard (the owner is always signed in, so no
   * login is triggered). Distinct from the display-only `creatorName` /
   * blocker-card avatar, which are computed differently (`storeDisplayName`).
   */
  creatorInfo?: CreatorInfo;
}

export const CommunityContext = createContext<CommunityContextValue | null>(
  null,
);
